/**
 * FairProductCardV2 — RedisiginProductPage Product card `1:626`
 * States: Default / Hovered / Opened actions · Size Default / Mobile
 */

export const FAIR_PRODUCT_CARD_V2_SIZES = ['default', 'mobile'] as const
export type FairProductCardV2Size = (typeof FAIR_PRODUCT_CARD_V2_SIZES)[number]

export const FAIR_PRODUCT_CARD_V2_STATES = [
  'default',
  'hovered',
  'opened',
] as const
export type FairProductCardV2State = (typeof FAIR_PRODUCT_CARD_V2_STATES)[number]

export type FairProductCardV2PreviewState = FairProductCardV2State

export interface FairProductCardV2ColorDot {
  /**
   * A CSS colour, or `null` when the API could not resolve a swatch — the dot
   * then renders a neutral placeholder. The backend never invents a colour, so
   * neither does the card.
   */
  color: string | null
  label?: string
}

/** Dots a card draws before "+N" takes over. */
export const FAIR_PRODUCT_CARD_V2_DOTS_SHOWN = 3

/**
 * How many colourways stay unshown once the dots are drawn.
 *
 * `total` is the honest count from the API, which can exceed the chips it sent
 * (they are capped for display) — so the overflow is counted against the total,
 * never against the chips array length.
 */
export function fairProductCardV2ColorOverflow(total: number | undefined, drawn: number): number {
  if (!total || total <= drawn) return 0
  return total - drawn
}

export function normalizeFairProductCardV2Page(
  page: number,
  total: number,
): number {
  const safeTotal = Math.max(1, Math.floor(total))
  return Math.min(Math.max(0, Math.floor(page)), safeTotal - 1)
}

/** Desktop hover/open or mobile always — overlay chrome visible */
export function fairProductCardV2ShowsChrome(
  size: FairProductCardV2Size,
  state: FairProductCardV2State,
): boolean {
  if (size === 'mobile') return true
  return state === 'hovered' || state === 'opened'
}

/** Gallery arrows only on desktop hover/open (Figma Mobile Default has none) */
export function fairProductCardV2ShowsArrows(
  size: FairProductCardV2Size,
  state: FairProductCardV2State,
  total: number,
): boolean {
  return (
    size === 'default'
    && (state === 'hovered' || state === 'opened')
    && total > 1
  )
}

/** T-129 — the mobile card shows one photo: no pager (it stays on the PDP gallery only). */
export function fairProductCardV2ShowsPagination(
  size: FairProductCardV2Size,
  state: FairProductCardV2State,
  total: number,
): boolean {
  if (total <= 1 || size === 'mobile') return false
  return fairProductCardV2ShowsChrome(size, state)
}
